import type {
  BreakerSelection,
  PortOutputFlowInterface,
  PortsMapInterface,
  UnitInterface,
} from '../units/types';
import type { ConnectionInterface } from '../connections/types';
import type { FeederScreenDeckMap } from 'unitData/Feeders/FeederScreen/FeederScreenTypes';
import type {
  RiskAssessmentInfoInterface,
  ScreenRiskAssessmentInfoInterface,
} from './types/reportInfo';

export const SIMULATE = 'SIMULATE';
export const SIMULATION_RESULT = 'SIMULATION_RESULT';

export interface SimulationRequestInterface {
  units: UnitInterface[];
  portMapping: PortsMapInterface;
  connections: ConnectionInterface[];
  globalConfig: {
    calcUnits: string;
    screenCalcMode: string;
    applicationType?: string;
  };
}

export interface SimulationResponseUnitInterface {
  key: string;
  warnings: WarningsInterface;
  unitInfo: SimulationResponseUnitInfoInterface;
  reportInfo: ReportSimulationInterface;
  riskAssessmentInfo?:
    RiskAssessmentInfoInterface | ScreenRiskAssessmentInfoInterface;
}

export interface SimulationResponseUnitInfoInterface {
  calcMinCSS: number;
  sieveSizeP80: number;
  load: number;
  loadRight?: number;
  loadRightMax?: number;
  loadLeft?: number;
  rotorCapacity?: number;
  rotorLoad?: number;
  biFlow?: number;
  biFlowTonnage?: number;
  liveBinVolume?: number;
  oversizePossible?: boolean;
  oversizeAmount?: number;
  productPercentage?: number;
  retainedAmount?: number;
  tphLeft?: number;
  tphRight?: number;
  tphMiddle?: number;
  binFeederUsed?: boolean;
  feederModelRight?: string;
  feederModelLeft?: string;
  modelName?: string;
  p3_50: number;
  p4_50: number;
  p5_50: number;
  p6_50: number;
  p1_20: number;
  p2_20: number;
  p3_20: number;
  p4_20: number;
  p5_20: number;
  p6_20: number;
  s1: number;
  s2: number;
  s3: number;
  c1: number;
  c2: number;
  c3: number;
  c4: number;
  p1_80: number;
  p2_80: number;
  p3_80: number;
  p4_80: number;
  p5_80: number;
  p6_80: string;
  p1_50: number;
  p2_50: number;
  selectedBreakers?: BreakerSelection;
  feederLoad: number;
  crusherLoad: number;
  screenLoad: number;
  closedCircuitFlowRate: number;
  decks?: DeckMapEfficiencyInterface;
}

export interface ReportSimulationInterface extends SimulationResponseUnitInfoInterface {}

export interface SimulationResponsePortInterface {
  key: string;
  unitKey: string;
  outputFlow: PortOutputFlowInterface;
}

export interface PerformanceGuaranteeResponseInterface {
  message: string;
  filename: string;
}

export interface SimulationResponseInterface {
  outputFlowMass: number;
  units: SimulationResponseUnitInterface[];
  ports: SimulationResponsePortInterface[];
}

export interface SimulationResultInterface {
  warnings: WarningsInterface | undefined;
  simulationUnitInfo: SimulationResponseUnitInfoInterface | undefined;
  reportInfo?: ReportSimulationInterface;
  riskAssessmentInfo?:
    RiskAssessmentInfoInterface | ScreenRiskAssessmentInfoInterface;
}

export interface WarningsInterface {
  MINOR: SingleWarningInterface[];
  MODERATE: SingleWarningInterface[];
  MAJOR: SingleWarningInterface[];
  INFO?: SingleWarningInterface[];
}

export interface SingleWarningInterface {
  message: string;
  level: string;
}

export interface DeckSimulationNormalInterace {
  separationSize: number;
  screeningMediaId: string;
  overUnderSize: string;
}
export interface DeckSimulationEfficiencyInterace {
  separationSize: number;
  screeningMediaId: string;
  openArea: number;
  particleShape: string;
  efficiencyParams: { mode: string; value?: number };
  efficiency: number;
}
export interface DeckMapNormalInterface {
  [deckIndex: string]: DeckSimulationNormalInterace;
}
export interface DeckMapEfficiencyInterface {
  [deckIndex: string]: DeckSimulationEfficiencyInterace;
}

export interface UnitInfoScreenSimulationInterface {
  screenModel: string;
  nbrOfDecks: number;
  decks: DeckMapNormalInterface | DeckMapEfficiencyInterface;
  screenFamily: string;
  sizeOption?: string;
  inclination: number;
  deckOneRelieving: boolean;
  materialType: string;
  wetScreening: boolean;
  parallelUnits: number;
  newMoisture: boolean;
  moisture?: number;
}

export interface UnitInfoFeederScreenSimulationInterface {
  modelName: string;
  noOfDecks: number;
  decks: FeederScreenDeckMap;
  parallelUnits: number;
  newMoisture: boolean;
  moisture?: number;
}

export interface UnitInfoGrizzlyScreenSimulationInterface {
  modelName: string;
  decks: GrizzlyScreenRequestDeckMapInterface;
  materialType: string;
  parallelUnits: number;
  newMoisture: boolean;
  moisture?: number;
}

export interface GrizzlyScreenRequestDeckMapInterface {
  [deckIndex: number]: GrizzlyScreenRequestDeckInterface;
}

export interface GrizzlyScreenRequestDeckInterface {
  separationSize: number;
  screeningMediaId?: string;
  overUnderSize?: string;
  particleShape?: string;
  openArea?: number;
  efficiencyParams?: RequestEfficiencyParamsInterface;
}

export interface RequestEfficiencyParamsInterface {
  mode: string;
  value?: number;
}
